import { isAffirmative, isNegative, normalizeVoiceText, parseVoiceDateRange, parseVoiceNumber } from "../../voice/ownerVoiceParsers";
import {
  chooseEntity,
  currencyFromVoice,
  idOf,
  includesAny,
  spokenDate,
  spokenMoney,
  voiceChoiceIndex,
} from "../ownerJarvisUtils";

function financeCategoryCandidates(options = {}) {
  const byName = new Map();
  const add = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    const key = normalizeVoiceText(clean);
    if (!key || byName.has(key)) return;
    byName.set(key, { name: clean });
  };
  (options.categories || []).filter((row) => !row.kind || row.kind === "expense" || row.kind === "both").forEach((row) => add(row.name));
  (options.mostUsedCategories || []).filter((row) => row.type === "expense").forEach((row) => add(row.category));
  (options.recentCategories || []).filter((row) => row.type === "expense").forEach((row) => add(row.category));
  return [...byName.values()];
}

function incomeCategoryCandidates(options = {}) {
  const byName = new Map();
  const add = (name, row = {}) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    const key = normalizeVoiceText(clean);
    if (!key || byName.has(key)) return;
    byName.set(key, { ...row, name: clean });
  };
  (options.categories || []).filter((row) => !row.kind || row.kind === "income" || row.kind === "both").forEach((row) => add(row.name, row));
  (options.mostUsedCategories || []).filter((row) => row.type === "income").forEach((row) => add(row.category, { kind: "income", uses: row.uses }));
  (options.recentCategories || []).filter((row) => row.type === "income").forEach((row) => add(row.category, { kind: "income" }));
  return [...byName.values()];
}

function numberedCategoryPrompt(rows = []) {
  return rows.slice(0, 5).map((row, index) => `${index + 1}, ${row.name}`).join("; ");
}

function totalsForTransactions(rows = []) {
  const totals = {};
  rows.forEach((row) => {
    const currency = String(row.currency || "ARS").toUpperCase();
    totals[currency] = Number(totals[currency] || 0) + Number(row.amount || 0);
  });
  return totals;
}

function spokenTotals(totals = {}) {
  const parts = Object.entries(totals).filter(([, value]) => Number(value) > 0).map(([currency, value]) => spokenMoney(value, currency));
  return parts.length ? parts.join(" y ") : "cero";
}

function financeRangeLabel(range) {
  if (!range) return "en todo el histórico";
  if (range.label && !/^\d{4}-\d{2}-\d{2}$/.test(range.label)) return `en ${range.label}`;
  if (range.from === range.to) return `el ${spokenDate(range.from)}`;
  return `entre ${spokenDate(range.from)} y ${spokenDate(range.to)}`;
}

function financeHistoryBucket(range) {
  if (!range) return "month";
  const start = new Date(`${range.from}T12:00:00`);
  const end = new Date(`${range.to}T12:00:00`);
  const days = Math.max(1, Math.round((end - start) / 86400000) + 1);
  if (days <= 45) return "day";
  if (days <= 150) return "week";
  return "month";
}

export async function reviewOwnerJarvisIncomeCategory({ ownerApi, navigate, flowRef, category, range = null }) {
  const response = await ownerApi.listFinanceTransactions({ limit: 500, type: "income", category, from: range?.from || "", to: range?.to || "" });
  const rows = response?.transactions || [];
  const ids = rows.map(idOf).filter(Boolean);
  const totals = totalsForTransactions(rows);
  if (!rows.length) return { handled: true, message: `No encontré entradas de dinero en ${category}${range ? ` ${financeRangeLabel(range)}` : ""}.` };
  const params = new URLSearchParams();
  params.set("tipo", "income"); params.set("categoria", category); params.set("jarvisReview", "1");
  if (range?.from) params.set("desde", range.from); if (range?.to) params.set("hasta", range.to);
  navigate(`/dashboard/owner/movimientos?${params.toString()}`);
  flowRef.current = { stage: "review", category, transactionIds: ids, totals, firstOccurredAt: rows[rows.length - 1]?.occurredAt || null, lastOccurredAt: rows[0]?.occurredAt || null };
  return { handled: true, message: `Encontré ${rows.length} entradas en ${category} por ${spokenTotals(totals)}. Te las muestro juntas. ¿Qué querés hacer con este lote? Podés decir vinculalas al contrato de TuRazón, vinculalas a la mensualidad de TuRazón o vinculalas a un proyecto.` };
}

export function createOwnerJarvisFinanceProjectFlowCapability({ ownerApi, navigate, flowRef }) {
  const reviewIncomeCategory = (category, range = null) => reviewOwnerJarvisIncomeCategory({ ownerApi, navigate, flowRef, category, range });

  return {
    id: "finance-project-flow",
    async handle({ raw, text }) {
      const financeFlow = flowRef.current;

      if (financeFlow?.stage === "category-choice") {
        if (includesAny(text, ["cancelar", "dejalo", "ninguna"])) { flowRef.current = null; return { handled: true, message: "Listo, no toqué ninguna categoría." }; }
        const index = voiceChoiceIndex(raw, financeFlow.options?.length || 0);
        if (index < 0) return { handled: true, message: `Decime el número de la categoría que querés corregir: ${numberedCategoryPrompt(financeFlow.options)}.` };
        const selected = financeFlow.options[index];
        if (includesAny(text, ["usar", "mostrar", "ver", "esa esta bien"])) {
          flowRef.current = null;
          return reviewIncomeCategory(selected.name, financeFlow.range);
        }
        flowRef.current = { ...financeFlow, stage: "category-rename", selected };
        return { handled: true, message: `Elegiste ${selected.name}. ¿Cómo querés que se llame? Decime solamente el nombre nuevo.` };
      }

      // Las etapas de escritura sensible se resuelven antes en finance-sensitive-writes.
      // Este handler legacy conserva solamente lectura, preguntas y transiciones sin mutación.
      if (financeFlow?.stage === "category-rename") return null;

      if (financeFlow?.stage === "review") {
        if (includesAny(text, ["linkear", "vincular", "asociar", "imputar"]) && /\b(?:mensualidad|mensual|servidor|hosting|abono)\b/.test(text)) {
          flowRef.current = { ...financeFlow, stage: "monthly-project-name" };
          return { handled: true, message: "Perfecto. Estos ingresos van al histórico de la mensualidad/servidor y no al contrato base. ¿De qué proyecto es esa mensualidad? Decime solamente el nombre, por ejemplo TuRazón." };
        }
        if (includesAny(text, ["linkear", "vincular", "asociar", "imputar"]) && /\b(?:contrato|cuotas?|down payment)\b/.test(text)) {
          flowRef.current = { ...financeFlow, stage: "contract-project-name" };
          return { handled: true, message: "Perfecto. Estos ingresos van al contrato, no a la mensualidad. ¿De qué proyecto es ese contrato? Decime solamente el nombre, por ejemplo TuRazón." };
        }
        if (includesAny(text, ["linkear", "vincular", "asociar"]) && /\b(?:proyecto|iniciativa|frente)\b/.test(text)) {
          flowRef.current = { ...financeFlow, stage: "project-exists" };
          return { handled: true, message: "Perfecto. ¿Ese proyecto ya está creado? Decime sí y el nombre, o no todavía." };
        }
        if (includesAny(text, ["cancelar", "dejalo", "nada"])) { flowRef.current = null; return { handled: true, message: "Listo. Dejé esos cobros solamente filtrados, sin cambiar ninguna relación." }; }
      }

      if (financeFlow?.stage === "contract-project-name") return null;
      if (financeFlow?.stage === "monthly-project-name") return null;

      if (financeFlow?.stage === "project-exists") {
        if (isNegative(raw) || includesAny(text, ["no esta creado", "no existe", "todavia no"])) {
          flowRef.current = { ...financeFlow, stage: "new-project-name" };
          return { handled: true, message: "Bien. ¿Cómo querés llamar al proyecto?" };
        }
        return null;
      }

      if (financeFlow?.stage === "existing-project-name") return null;

      if (financeFlow?.stage === "new-project-name") return null;

      if (financeFlow?.stage === "payment-attribution") {
        if (includesAny(text, ["contrato base", "base", "contrato existente", "lo de siempre"])) return null;
        if (includesAny(text, ["nuevo bloque", "bloque nuevo", "nuevo termino", "termino nuevo", "ampliacion", "aumento"])) {
          flowRef.current = { ...financeFlow, stage: "term-title", attributeSelectionToNewTerm: true };
          return { handled: true, message: "Bien. ¿Cómo querés llamar a ese nuevo bloque o término?" };
        }
        return { handled: true, message: "Decime contrato base si son pagos del acuerdo existente, o nuevo bloque si corresponden a una ampliación." };
      }

      if (financeFlow?.stage === "contract-question") {
        if (isNegative(raw)) { flowRef.current = null; return { handled: true, message: "Perfecto. Los cobros ya quedaron vinculados al proyecto. El contrato lo podemos completar después desde la ficha." }; }
        if (isAffirmative(raw) || includesAny(text, ["configurar", "armar contrato", "contrato base"])) {
          flowRef.current = { ...financeFlow, stage: "contract-mode" };
          return { handled: true, message: "¿El acuerdo base era mensual, por un monto fijo o en cuotas?" };
        }
      }

      if (financeFlow?.stage === "contract-mode") {
        let billingMode = "";
        if (/\b(?:mensual|mes|mensualidad)\b/.test(text)) billingMode = "monthly";
        else if (/\b(?:cuota|cuotas)\b/.test(text)) billingMode = "installments";
        else if (/\b(?:fijo|unico|único|total)\b/.test(text)) billingMode = "one_time";
        if (!billingMode) return { handled: true, message: "Decime mensual, monto fijo o cuotas." };
        flowRef.current = { ...financeFlow, stage: "contract-amount", billingMode };
        return { handled: true, message: billingMode === "monthly" ? "¿Cuánto era el monto acordado por mes?" : "¿Cuál era el monto acordado?" };
      }

      if (financeFlow?.stage === "contract-amount") return null;

      if (financeFlow?.stage === "more-terms") {
        if (isNegative(raw)) { flowRef.current = null; return { handled: true, message: "Perfecto. El proyecto ya tiene contrato base y pagos históricos sincronizados. Podemos agregar nuevos términos cuando aparezcan." }; }
        if (isAffirmative(raw) || includesAny(text, ["aumento", "nuevo termino", "nuevo bloque", "ampliacion"])) {
          flowRef.current = { ...financeFlow, stage: "term-title" };
          return { handled: true, message: "¿Cómo querés llamar a ese nuevo bloque o término?" };
        }
      }

      if (financeFlow?.stage === "term-title") {
        const termTitle = String(raw || "").trim().replace(/^jarvis[,:]?\s*/i, "").slice(0, 180);
        if (!termTitle) return { handled: true, message: "Decime el nombre del bloque." };
        flowRef.current = { ...financeFlow, stage: "term-amount", termTitle };
        return { handled: true, message: "¿Cuánto agrega ese bloque o término al acuerdo?" };
      }

      if (financeFlow?.stage === "term-amount") {
        const amount = parseVoiceNumber(raw);
        if (!(amount >= 0)) return { handled: true, message: "Repetíme el monto del nuevo término." };
        const currency = currencyFromVoice(raw);
        flowRef.current = { ...financeFlow, stage: "term-task", termAmount: amount, termCurrency: currency };
        return { handled: true, message: `Anoto ${spokenMoney(amount, currency)}. ¿Querés que además cree una tarea vinculada a este bloque? Si sí, decime el título de la tarea. Si no, decime no.` };
      }

      if (financeFlow?.stage === "term-task") return null;

      const asksIncomeCategory = /\b(?:mostra|mostrame|mostrar|ver)\b/.test(text)
        && /\b(?:entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros)\b/.test(text);
      if (!asksIncomeCategory) return null;

      const options = await ownerApi.financeMovementOptions();
      const categories = incomeCategoryCandidates(options);
      const result = chooseEntity(categories, text, ["name"]);
      const range = parseVoiceDateRange(raw);
      if (result.row) return reviewIncomeCategory(result.row.name, range);
      const fallback = (result.alternatives?.length ? result.alternatives : categories).slice(0, 5);
      if (fallback.length) {
        flowRef.current = { stage: "category-choice", options: fallback, range, originalCommand: raw };
        return { handled: true, message: `No encontré esa categoría exactamente. Si querés corregir una de las que ya existen, elegí por número: ${numberedCategoryPrompt(fallback)}. Decime el número para renombrarla, o por ejemplo usar 2 si sólo querés revisar esa.` };
      }
      return { handled: true, message: "No encontré categorías de ingreso todavía. Podés decir crear categoría y la armamos sin perder el resto de Owner 360." };
    },
  };
}

export function createOwnerJarvisFinanceQueryCapability({ ownerApi, ownerFinanceStatsApi, navigate }) {
  return {
    id: "finance-query",
    async handle({ raw, text }) {
      const asksAccountBalance = /\b(?:cuanto|cuanta|saldo)\b/.test(text)
        && /\b(?:plata|saldo|deberia tener|debo tener|tengo en|hay en)\b/.test(text)
        && !/\b(?:gaste|gastado|gasto|pague|pagado|pago)\b/.test(text);
      if (asksAccountBalance) {
        const response = await ownerApi.listFinanceAccounts();
        const accounts = response?.accounts || [];
        const result = chooseEntity(accounts, text, ["name", "type", "currency"]);
        if (result.row) {
          const account = result.row;
          const accountId = idOf(account);
          navigate(`/dashboard/owner/movimientos?cuenta=${encodeURIComponent(accountId)}`);
          return {
            handled: true,
            message: `En ${account.name} deberías tener ${spokenMoney(account.balance, account.currency || "ARS")}. Te abro la caja para que veas saldo inicial y los movimientos que explican ese saldo.`,
          };
        }
        if (result.alternatives?.length) {
          return { handled: true, message: `Encontré varias cajas parecidas: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál.` };
        }
        return { handled: true, message: "No encontré esa caja. Decime el nombre como aparece en Mi plata." };
      }

      const asksExpenseByCategory = /\b(?:cuanto|cuanta|cuantos)\b/.test(text)
        && /\b(?:gaste|gastado|gasto|pague|pagado|pago|pagar|se fue)\b/.test(text);
      if (!asksExpenseByCategory) return null;

      const [optionsResponse, accountsResponse] = await Promise.all([
        ownerApi.financeMovementOptions(),
        ownerApi.listFinanceAccounts(),
      ]);
      const categories = financeCategoryCandidates(optionsResponse);
      const result = chooseEntity(categories, text, ["name"]);
      if (result.row) {
        const category = result.row.name;
        const range = parseVoiceDateRange(raw);
        const currencies = [...new Set((accountsResponse?.accounts || []).map((row) => String(row.currency || "").toUpperCase()).filter(Boolean))];
        if (!currencies.length) currencies.push("ARS");
        const totals = await Promise.all(currencies.slice(0, 4).map(async (currency) => {
          const response = await ownerFinanceStatsApi.history({
            start: range?.from || "",
            end: range?.to || "",
            currency,
            category,
            bucket: financeHistoryBucket(range),
          });
          return { currency, amount: Number(response?.stats?.summary?.expense || 0), count: Number(response?.stats?.summary?.count || 0) };
        }));
        const nonZero = totals.filter((row) => row.amount > 0);
        const primary = nonZero.find((row) => row.currency === "ARS") || nonZero[0] || totals.find((row) => row.currency === "ARS") || totals[0];
        const params = new URLSearchParams();
        params.set("vista", "historico");
        params.set("categoria", category);
        if (primary?.currency) params.set("moneda", primary.currency);
        if (range?.from && range?.to) {
          params.set("desde", range.from);
          params.set("hasta", range.to);
        }
        navigate(`/dashboard/owner/estadisticas?${params.toString()}`);
        if (!nonZero.length) return { handled: true, message: `No encontré gastos en ${category} ${financeRangeLabel(range)}. Igual te abro Estadísticas con ese filtro para revisarlo.` };
        const spokenCategoryTotals = nonZero.map((row) => spokenMoney(row.amount, row.currency)).join(" y ");
        const movementCount = nonZero.reduce((sum, row) => sum + Number(row.count || 0), 0);
        return { handled: true, message: `En ${category} gastaste ${spokenCategoryTotals} ${financeRangeLabel(range)}${movementCount ? ` en ${movementCount} ${movementCount === 1 ? "movimiento" : "movimientos"}` : ""}. Te abro Estadísticas ya filtrado para que veas de dónde sale.` };
      }
      if (result.alternatives?.length) {
        return { handled: true, message: `Encontré categorías parecidas: ${result.alternatives.map((row) => row.name).join(", ")}. Decime cuál querés revisar.` };
      }
      return null;
    },
  };
}
